import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";

import { Logo } from "./Logo";
import { LoginArea } from "@/components/auth/LoginArea";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ADMIN_PUBKEY } from "@/lib/cashcraft";

interface NavItem {
  label: string;
  href: string;
  /** True for in-app routes (react-router), false for same-page anchors. */
  route?: boolean;
  admin?: boolean;
  primary?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: "How it works", href: "/#how-it-works" },
  { label: "Our work", href: "/portfolio", route: true },
  { label: "Pricing", href: "/#pricing" },
  { label: "Revisions", href: "/#revisions" },
  { label: "Hosting", href: "/hosting", route: true },
  { label: "FAQ", href: "/#faq" },
  { label: "Orders", href: "/orders", route: true, admin: true, primary: true },
];

export function SiteHeader() {
  const { user } = useCurrentUser();
  const isAdmin = user?.pubkey === ADMIN_PUBKEY;
  const [open, setOpen] = useState(false);

  const items = NAV_ITEMS.filter((item) => !item.admin || isAdmin);

  const linkClass = (primary?: boolean) =>
    `text-sm font-medium transition-colors ${
      primary
        ? "text-primary hover:text-primary/80"
        : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/80 backdrop-blur-lg">
      <div className="container mx-auto flex h-16 items-center justify-between gap-4 px-4 sm:px-6">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 md:flex">
          {items.map((item) =>
            item.route ? (
              <Link key={item.label} to={item.href} className={linkClass(item.primary)}>
                {item.label}
              </Link>
            ) : (
              <a key={item.label} href={item.href} className={linkClass(item.primary)}>
                {item.label}
              </a>
            ),
          )}
        </nav>

        <div className="flex items-center gap-2">
          <Button asChild size="sm" className="hidden sm:inline-flex rounded-full font-semibold">
            <a href="/#start">Start a project</a>
          </Button>
          <LoginArea className="max-w-52" />

          {/* Mobile menu trigger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle className="text-left">
                  <Logo />
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-1">
                {items.map((item) =>
                  item.route ? (
                    <Link
                      key={item.label}
                      to={item.href}
                      onClick={() => setOpen(false)}
                      className={`rounded-lg px-3 py-3 text-base font-medium transition-colors hover:bg-accent ${
                        item.primary ? "text-primary" : "text-foreground"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={() => setOpen(false)}
                      className="rounded-lg px-3 py-3 text-base font-medium text-foreground transition-colors hover:bg-accent"
                    >
                      {item.label}
                    </a>
                  ),
                )}
                <Button
                  asChild
                  className="mt-4 w-full rounded-full font-semibold"
                  onClick={() => setOpen(false)}
                >
                  <a href="/#start">Start a project</a>
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
